import type { Asset, AssetId, AssetKind } from '../state';
import {
  ASSET_CACHE_BUDGET_BYTES,
  evictAssetDataLRU,
  touchAsset,
} from './asset-cache';
import { hashImageData } from './content-hash';

/**
 * Result returned by registerAsset. All fields are plain data - no
 * mutation of the inputs.
 */
export type AssetRegisterResult = {
  /** Updated library (unchanged reference on dedup hit). */
  assetLibrary: Asset[];
  /** Updated data map (unchanged reference on dedup hit). */
  assetData: Map<string, ImageData>;
  /** Updated LRU order for assetData. */
  assetDataAccessOrder: string[];
  /** The registered entry (existing or newly created). */
  asset: Asset;
  /** true when a new entry was added; false when existing entry was reused. */
  wasNew: boolean;
};

/**
 * Register an ImageData into the asset library.
 *
 * Computes a content-hash from the pixel data (including dimensions),
 * builds a kind-scoped id (`${kind}:${contentHash}`), and looks for an
 * existing entry. On a dedup hit the inputs are returned unchanged so
 * callers can use referential equality to skip a store.patch when
 * nothing changed.
 *
 * Pure function - no DOM, no side effects, no console.log.
 * The caller uses the returned values to call `store.patch(...)`.
 */
export function registerAsset(
  currentLibrary: readonly Asset[],
  currentData: ReadonlyMap<string, ImageData>,
  currentAccessOrder: readonly string[],
  image: ImageData,
  kind: AssetKind,
  budgetBytes = ASSET_CACHE_BUDGET_BYTES,
): AssetRegisterResult {
  const contentHash = hashImageData(image);
  const id = `${kind}:${contentHash}` as AssetId;

  // Dedup check - if the library already contains this exact asset, return
  // the existing entry. If the binary is already present too, leave both
  // inputs unchanged. If the binary is missing (metadata-only state, e.g.
  // after a preset load), restore it into a new Map so the caller can
  // persist it, while still reporting wasNew: false (metadata unchanged).
  const existing = currentLibrary.find((a) => a.id === id);
  if (existing !== undefined) {
    if (currentData.has(id)) {
      return {
        assetLibrary: currentLibrary as Asset[],
        assetData: currentData as Map<string, ImageData>,
        assetDataAccessOrder: touchAsset(currentAccessOrder, id),
        asset: existing,
        wasNew: false,
      };
    }
    // Binary missing - restore it without touching the library reference.
    const restoredData = new Map(currentData);
    restoredData.set(id, image);
    const restoredOrder = touchAsset(currentAccessOrder, id);
    const evicted = evictAssetDataLRU(restoredData, restoredOrder, budgetBytes);
    return {
      assetLibrary: currentLibrary as Asset[],
      assetData: evicted.assetData,
      assetDataAccessOrder: evicted.accessOrder,
      asset: existing,
      wasNew: false,
    };
  }

  // New asset - build the metadata entry and extend both collections.
  const newAsset: Asset = {
    id,
    kind,
    contentHash,
    width: image.width,
    height: image.height,
    uploadTs: Date.now(),
  };

  const newLibrary: Asset[] = [...currentLibrary, newAsset];
  const newData = new Map(currentData);
  newData.set(id, image);
  const newOrder = touchAsset(currentAccessOrder, id);
  const evicted = evictAssetDataLRU(newData, newOrder, budgetBytes);

  return {
    assetLibrary: newLibrary,
    assetData: evicted.assetData,
    assetDataAccessOrder: evicted.accessOrder,
    asset: newAsset,
    wasNew: true,
  };
}
